import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { joinWaitlist, getUser, signIn, signOutUser, sendPasswordReset } from '@wave-circuit/core';
import { useAuth } from '../../app/AuthContext';
import { memberHomePath } from '../../app/MemberRedirect';
import { Sparkles, AlertCircle } from 'lucide-react';
import Logo from '../../assets/images/logo.png';
import './LoginPage.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // The page a signed-out visitor was trying to open, if any (set by PrivateRoute).
  const returnTo = location.state?.from;
  const { refresh } = useAuth();

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first, then click Forgot Password.');
      return;
    }
    try {
      await sendPasswordReset(email.trim());
      setResetSent(true);
      setError('');
    } catch (err) {
      setError('Failed to send reset email. Please check your email address.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const cleanEmail = email.trim().toLowerCase();

    try {
      await signIn(cleanEmail, password);

      // Beta gate: only accounts with the member claim (set when an invited user
      // signs up) may stay signed in. Everyone else goes on the waitlist.
      const claims = await refresh();
      if (!claims.member || !claims.profileId) {
        await signOutUser();
        await joinWaitlist({ email: cleanEmail }).catch(() => {});
        setError('Wave Circuit is currently in private beta for invited guests only. We have registered your email to the waitlist and will notify you when access is granted!');
        setLoading(false);
        return;
      }

      if (returnTo?.pathname) {
        navigate(`${returnTo.pathname}${returnTo.search || ''}`, { replace: true });
        return;
      }
      navigate(memberHomePath((await getUser(claims.profileId)) || {}));
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password. If you do not have an invited account, please join the beta waitlist.');
      } else {
        setError(err.message || 'Failed to sign in.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container id='signInContainer'>
        <div id='intro'>
          <img src={Logo} id='homeLogo' alt='Wave Logo' />
        </div>

        <div id='login'>
          <Form onSubmit={handleSubmit}>
            <div className="text-center mb-3">
              <span className="badge rounded-pill bg-info bg-opacity-10 text-info px-3 py-1 mb-2" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                <Sparkles size={12} className="inlineIcon me-1" /> PRIVATE BETA
              </span>
            </div>

            <Form.Group className="mb-3" id='userGroup'>
              <Form.Label className='loginLabels'>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your registered email"
                id='username'
                className='loginInput'
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-4" id='passGroup'>
              <Form.Label className='loginLabels'>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Password"
                className='loginInput'
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p id='forgotPass'>
                <a href="#!" onClick={(e) => { e.preventDefault(); handleForgotPassword(); }}>Forgot password?</a>
              </p>
              {resetSent && <p style={{ color: 'green', fontSize: '0.875rem' }}>Password reset email sent! Check your inbox.</p>}
            </Form.Group>

            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '10px 12px',
                marginBottom: '1rem',
                color: '#ef4444',
                fontSize: '0.875rem'
              }}>
                <AlertCircle size={16} className="inlineIcon me-1" /> {error}
              </div>
            )}

            <div className="text-center pt-1 mb-5 pb-1 d-flex flex-column gap-3 align-items-center" id='loginButtons'>
              <Button 
                className="btn-custom btn-custom-primary w-100" 
                id='signIn' 
                type="submit" 
                disabled={loading}
              >
                {loading ? 'Verifying Access...' : 'Sign In'}
              </Button>
              <Button 
                className="btn-custom btn-custom-secondary w-100" 
                id='createAcct' 
                onClick={() => navigate('/createAccount')}
              >
                Join Beta / Create Account
              </Button>
            </div>
          </Form>
        </div>
      </Container>
    </>
  );
}

export default Login;